import Image from 'next/image'
import { useEffect, useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'

import MapMarkerIcon from '../../../../../public/assets/mapMarker'
import { fetchAdvisers } from '../lib/findAdvisers'
import { fetchOffices } from '../lib/findOffices'
import styles from '../Searchfindcomponent.module.css'

interface Office {
  uuid: string
  name: string
  full_slug: string
  content: {
    component: 'advanceOffice' | 'office'
    address?: string
    city?: string
    state?: string
    postcode?: string
    Phone?: string
    office?: Array<{
      address: string
      phone: string
    }>
  }
}

interface Adviser {
  uuid: string
  name: string
  full_slug: string
  content: {
    filename?: string
    image?: {
      filename: string
    }
    title: string
    specialities: string[]
  }
}

interface OfficesProps {
  name?: string
  selectedSpecialty?: string
  specitlyOffices?: string[] | null
}

const Offices: React.FC<OfficesProps> = ({ specitlyOffices, selectedSpecialty }) => {
  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION ?? 'au'
  const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

  const [officeLocations, setOfficeLocations] = useState<Office[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalOffices, setTotalOffices] = useState(0)
  const [loadingAdvisers, setLoadingAdvisers] = useState<boolean>(false)
  const [advisersData, setAdvisersData] = useState<Adviser[]>([])
  const [openOffice, setOpenOffice] = useState<string | null>(null)

  const getOfficeLocations = async (page = 1) => {
    try {
      setIsLoading(true)
      const officesUUIDs = specitlyOffices?.map((office) => office).join(',')
      const { offices, total } = await fetchOffices({
        officesUUIDs: officesUUIDs != null && officesUUIDs.length > 0 ? officesUUIDs : undefined,
        location,
        filterLocation,
        paginate: true,
        page,
        perPage: 10,
      })

      if (page === 1) {
        setOfficeLocations(offices as unknown as Office[])
      } else {
        setOfficeLocations((prev) => [...prev, ...(offices as unknown as Office[])])
      }
      setTotalOffices(total ?? 0)
      setCurrentPage(page)
    } catch (error) {
      console.error('Error fetching office locations:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadMore = () => {
    getOfficeLocations(currentPage + 1)
  }

  // Fetch offices when specialty changes
  useEffect(() => {
    setOfficeLocations([])
    setCurrentPage(1)
    setTotalOffices(0)
    setLoadingAdvisers(false)
    setAdvisersData([])

    // Only load offices if:
    // 1. No specialty is selected (show all offices), OR
    // 2. Specialty is selected AND we have filtered office UUIDs available
    if (selectedSpecialty === '' || (selectedSpecialty !== '' && specitlyOffices !== null)) {
      getOfficeLocations()
    }
  }, [selectedSpecialty, specitlyOffices])

  // pass current selected office to get phone if required
  const renderAdvisers = (currentOffice: Office) => {
    if (loadingAdvisers) {
      return (
        <div className={styles['loadingAdvisers']}>
          <div className={styles['spinner']}></div>
          <p>Loading advisers...</p>
        </div>
      )
    }

    return (
      <ul className={styles['advisersList']}>
        {advisersData.map((advisor) => {
          return (
            <div key={advisor.uuid} className={styles['officeAdviserComponent']}>
              <a href={`/${advisor.full_slug}`} className={styles['image']}>
                {(Boolean((advisor.content.filename as string)?.trim()) ||
                  Boolean((advisor.content.image as { filename?: string })?.filename?.trim())) && (
                  <Image
                    unoptimized={true}
                    src={
                      (advisor.content.filename as string)?.trim() ||
                      ((advisor.content.image as { filename?: string })?.filename?.trim() ?? '') ||
                      ''
                    }
                    alt={advisor.name ?? 'Adviser'}
                    width={120}
                    height={120}
                    objectFit="contain"
                    loading="lazy"
                    placeholder="blur"
                    blurDataURL="data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20120%20120%22%2F%3E"
                  />
                )}
              </a>
              <div className={styles['name']}>
                <h5>{advisor.name ?? 'Adviser'}</h5>
                <h6>{advisor.content?.title ?? ''}</h6>
              </div>
              <div className={styles['phone']}>
                <h5>Phone</h5>
                <h6>
                  <a href={`tel:${currentOffice?.content?.Phone ?? ''}`}>
                    {currentOffice?.content?.Phone ?? ''}
                  </a>
                </h6>
              </div>
              <div className={styles['speciality']}>
                <h5>My Specialty</h5>
                <span>
                  {advisor.content?.specialities
                    ?.map((speciality: string) => {
                      return speciality
                    })
                    ?.join(', ') ?? ''}
                </span>
              </div>
            </div>
          )
        })}
      </ul>
    )
  }

  const handleToggleAdvisers = async (officeId: string) => {
    setOpenOffice((prevOfficeId) => (prevOfficeId === officeId ? null : officeId))

    // set adaviserdata to empty array
    setAdvisersData([])

    const advisers = await fetchAdvisers(officeId)
    setAdvisersData(advisers)
  }

  return (
    <div className={styles['officesView']}>
      <div className={styles['bottomContainer']}>
        <div className={styles['bottomInner']}>
          {officeLocations.map((office) => {
            const isOpen = openOffice === office.uuid
            return (
              <div key={office.uuid} className={styles['office']}>
                <div className={styles['officeName']}>
                  <MapMarkerIcon className={styles['icon']} width={12} height={16} />

                  <a href={`/${office.full_slug}`}>Findex {office.name}</a>
                </div>
                <div className={styles['officeAddress']}>
                  <div>
                    {/* if office is advanceOffice, then show the address of the advanceOffice */}
                    {office.content.component === 'advanceOffice' &&
                    Array.isArray(office.content.office) &&
                    office.content.office.length > 0 ? (
                      <>
                        <p>{office.content.office[0].address}</p>
                      </>
                    ) : (
                      <>
                        <p>
                          {[
                            office.content.address,
                            [office.content.city, office.content.state, office.content.postcode]
                              .filter(Boolean)
                              .join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </>
                    )}
                  </div>
                  <a className={styles['officePhone']} href={`tel:${office.content.Phone}`}>
                    {office.content.component === 'advanceOffice' &&
                    Array.isArray(office.content.office) &&
                    office.content.office.length > 0 ? (
                      <p>Phone: {office.content.office[0].phone}</p>
                    ) : (
                      <p>Phone: {office.content.Phone}</p>
                    )}
                  </a>
                </div>
                <div className={styles['officeCta']}>
                  <PhnxButton
                    type="button"
                    variant="quaternary"
                    size="small"
                    label={isOpen ? 'Hide Advisers' : 'View Advisers'}
                    icon={isOpen ? 'fx-icon-arrow-up-lg' : 'fx-icon-arrow-down-lg'}
                    iconPosition="right"
                    onClick={() => handleToggleAdvisers(office.uuid)}
                  />
                </div>
                {isOpen && (
                  <>
                    {/* Render the list of advisers for this office */}
                    {renderAdvisers(office)}
                  </>
                )}
              </div>
            )
          })}
        </div>

        {officeLocations.length < totalOffices && officeLocations.length > 0 && (
          <div className={styles['loadMoreContainer']}>
            <PhnxButton
              type="button"
              variant="secondary"
              size="default"
              label={isLoading ? 'Loading...' : 'Load More'}
              onClick={loadMore}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default Offices
